import React from 'react';

const PrivacyPolicy = () => {
    return (
        <div className="container mx-auto p-6 max-w-4xl">
            <h1 className="text-4xl font-bold mb-6 text-gray-900">Privacy Policy</h1>
            <div className="prose prose-lg max-w-none">
                <p className="text-gray-600 mb-8">
                    Last Updated: January 2025
                </p>

                <div className="mb-8">
                    <p className="mb-4">
                        At CVSeeyou ("we," "our," or "us"), we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform and services.
                    </p>
                </div>

                <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-900">1. Information We Collect</h2>
                    <h3 className="text-xl font-semibold mb-3 text-gray-800">1.1 Personal Information</h3>
                    <ul className="list-disc pl-6 mb-4 space-y-2">
                        <li>Identity information (name, date of birth, nationality)</li>
                        <li>Contact details (email address, phone number, postal address)</li>
                        <li>Professional information (work history, education, qualifications, skills)</li>
                        <li>Account credentials (username, password hash)</li>
                        <li>Profile information (profile picture, professional summary)</li>
                    </ul>

                    <h3 className="text-xl font-semibold mb-3 text-gray-800">1.2 Documents and Content</h3>
                    <ul className="list-disc pl-6 mb-4 space-y-2">
                        <li>Resumes and CVs</li>
                        <li>Job descriptions and requirements</li>
                        <li>Portfolio materials</li>
                        <li>Professional certificates</li>
                    </ul>

                    <h3 className="text-xl font-semibold mb-3 text-gray-800">1.3 Technical Data</h3>
                    <ul className="list-disc pl-6 mb-4 space-y-2">
                        <li>Device and browser information</li>
                        <li>IP address and location data</li>
                        <li>Usage patterns and preferences</li>
                        <li>Cookies and similar tracking technologies</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-900">2. How We Use Your Information</h2>
                    <h3 className="text-xl font-semibold mb-3 text-gray-800">2.1 Core Service Provision</h3>
                    <ul className="list-disc pl-6 mb-4 space-y-2">
                        <li>Processing and storing resumes and job descriptions in encrypted storage</li>
                        <li>Generating AI embeddings for matching algorithms</li>
                        <li>Facilitating connections between job seekers and recruiters</li>
                        <li>Providing personalized job recommendations</li>
                        <li>Authenticating and maintaining your account</li>
                    </ul>

                    <h3 className="text-xl font-semibold mb-3 text-gray-800">2.2 Service Improvement</h3>
                    <ul className="list-disc pl-6 mb-4 space-y-2">
                        <li>Analyzing usage patterns to enhance our platform</li>
                        <li>Conducting research to improve our AI algorithms</li>
                        <li>Developing new features and services</li>
                    </ul>

                    <h3 className="text-xl font-semibold mb-3 text-gray-800">2.1 AI Processing with Gemini</h3>
                    <p className="mb-4">
                        We use Google's Gemini AI technology to process your information for the following purposes:
                    </p>
                    <ul className="list-disc pl-6 mb-4 space-y-2">
                        <li>Analyzing resumes and job descriptions to extract relevant information</li>
                        <li>Generating matches between candidates and job positions</li>
                        <li>Creating recommendations and insights</li>
                        <li>Optimizing search results and rankings</li>
                    </ul>
                    <p className="mb-4">
                        When your data is processed by Gemini AI:
                    </p>
                    <ul className="list-disc pl-6 mb-4 space-y-2">
                        <li>Data is processed in accordance with Google's AI principles and privacy standards</li>
                        <li>Processing occurs in secure, controlled environments</li>
                        <li>Data is not used to train or improve the AI model</li>
                        <li>Results are used solely for the purpose of providing our services</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-900">3. Third-Party AI Services</h2>
                    <p className="mb-4">
                        Our platform integrates with Google's Gemini AI technology. This means:
                    </p>
                    <ul className="list-disc pl-6 mb-4 space-y-2">
                        <li>Your data may be processed by Google's Gemini AI services</li>
                        <li>Processing is subject to Google's AI privacy and security standards</li>
                        <li>We maintain strict data handling agreements with Google</li>
                        <li>Data processing occurs in compliance with applicable privacy laws</li>
                    </ul>
                    <p className="mb-4">
                        For more information about Google's AI principles and data handling practices, you can visit Google's AI privacy documentation.
                    </p>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-900">3. Data Storage and Security</h2>
                    <p className="mb-4">
                        We implement robust security measures to protect your information:
                    </p>
                    <ul className="list-disc pl-6 mb-4 space-y-2">
                        <li>End-to-end encryption for all stored documents</li>
                        <li>Secure storage in AWS S3 buckets with enterprise-grade encryption</li>
                        <li>Regular security audits and penetration testing</li>
                        <li>Access controls and authentication mechanisms</li>
                        <li>Data backup and disaster recovery protocols</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-900">4. Data Retention</h2>
                    <p className="mb-4">
                        We retain your information for as long as necessary to provide our services and comply with legal obligations. You may request deletion of your data at any time, subject to legal requirements.
                    </p>
                    <p className="mb-4">
                        After account deletion:
                    </p>
                    <ul className="list-disc pl-6 mb-4 space-y-2">
                        <li>Personal information is permanently deleted within 30 days</li>
                        <li>Backup copies are removed within 90 days</li>
                        <li>Anonymized aggregate data may be retained for analysis</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-900">5. Your Privacy Rights</h2>
                    <p className="mb-4">
                        You have the following rights regarding your personal information:
                    </p>
                    <ul className="list-disc pl-6 mb-4 space-y-2">
                        <li>Right to access and receive a copy of your data</li>
                        <li>Right to rectification of inaccurate information</li>
                        <li>Right to erasure ("right to be forgotten")</li>
                        <li>Right to restrict processing</li>
                        <li>Right to data portability</li>
                        <li>Right to object to processing</li>
                        <li>Rights related to automated decision-making</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-900">6. International Data Transfers</h2>
                    <p className="mb-4">
                        We may transfer your information internationally. When we do, we ensure appropriate safeguards are in place through:
                    </p>
                    <ul className="list-disc pl-6 mb-4 space-y-2">
                        <li>Standard contractual clauses</li>
                        <li>Data processing agreements</li>
                        <li>Compliance with cross-border transfer regulations</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-900">7. Cookie Policy</h2>
                    <p className="mb-4">
                        We use cookies and similar technologies to enhance your experience. You can control cookie preferences through your browser settings.
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg mb-4">
                        <h3 className="text-xl font-semibold mb-2">Types of Cookies We Use:</h3>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>Essential cookies for platform functionality</li>
                            <li>Analytics cookies to improve our services</li>
                            <li>Preference cookies to remember your settings</li>
                        </ul>
                    </div>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-900">8. Changes to This Policy</h2>
                    <p className="mb-4">
                        We may update this Privacy Policy periodically. We will notify you of material changes via email or platform notification at least 30 days before implementation. Continued use of our services after changes constitutes acceptance of the updated Privacy Policy.
                    </p>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-bold mb-4 text-gray-900">9. Contact Us</h2>
                    <p className="mb-4">
                        If you have questions about this Privacy Policy or wish to exercise your privacy rights, please contact us:
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <p>Data Protection Officer</p>
                        <p>Email: privacy@cvseeyou.com</p>
                        <p>Address: [Your Company Address]</p>
                        <p>Phone: [Your Phone Number]</p>
                    </div>
                </section>

                <section className="mb-8 bg-blue-50 p-4 rounded-lg">
                    <p className="font-semibold">
                        For users in the European Union: The data controller for your information is CVSeeyou, [Legal Entity Name and Address]. You have the right to lodge a complaint with your local data protection authority.
                    </p>
                </section>
            </div>
        </div>
    );
};

export default PrivacyPolicy;